import { ReactNode } from 'react';
import styled from 'styled-components';

import { Text } from '../../../../cdl/Text/Text';
import { formatFromNow } from '../../../../common/helpers/dateFns.helper';
import { formatDateTime } from '../../../../common/helpers/formatDateTime.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { MarkerIcon } from '../../../../common/PositionMarker/PositionMarker';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import { Port } from '../../../../port/Port';
import { PositionType } from '../hooks/useOfferPositions';
import { PositionData } from '../types/PositionData';
import { getMarkerColor } from '../util/getMarkerColor';

const PositionItem = styled.li`
    display: flex;
    flex-direction: column;
    padding-left: 40px;
    position: relative;

    &:before {
        content: '';
        position: absolute;
        top: 16px;
        left: 18px;
        border-left: 2px dashed ${theme.colors.background.muted};
        height: 100%;
        width: 12px;
        z-index: ${theme.zIndex[100]};
    }

    &:first-of-type {
        &:before {
            top: 16px;
        }
    }

    &:last-of-type {
        &:before {
            border-left: none;
        }
    }

    &:only-of-type {
        &:before {
            border-left: none;
        }
    }

    &:hover {
        cursor: pointer;
        background-color: ${theme.colors.background.subtle};
    }
`;

const IconWrapper = styled(Box)`
    position: absolute;
    left: 11px;
    top: 11px;
    height: 16px;
    width: 16px;
    z-index: ${theme.zIndex[200]};
`;

interface PositionListItemWrapperProps {
    title: string;
    icon: ReactNode;
    children: ReactNode;
    disabled?: boolean;
}

const PositionListItemWrapper = ({ title, icon, children, disabled }: PositionListItemWrapperProps) => {
    return (
        <Box paddingBottom={8} paddingTop={8}>
            <IconWrapper>{icon}</IconWrapper>

            <Text variant="small" fontWeight={600} color={disabled ? 'foreground.disabled' : 'foreground.default'}>
                {title}
            </Text>

            <Box display="flex" flexDirection="column" color={disabled ? 'foreground.disabled' : 'foreground.default'}>
                {children}
            </Box>
        </Box>
    );
};

interface PositionProps {
    position: PositionData;
    onClick: () => void;
}

export const Position = ({ position, onClick }: PositionProps) => {
    const markerColor = getMarkerColor(position.type);

    switch (position.type) {
        case PositionType.ORDER_PORT:
            return (
                <PositionItem onClick={onClick}>
                    <PositionListItemWrapper
                        icon={<MarkerIcon color={markerColor} />}
                        title={
                            position.isAlsoNextPort
                                ? translate('offerPositions.orderAndNextPort')
                                : translate('offerPositions.orderPort')
                        }
                    >
                        <Port port={position.port} vesselId={position.vesselId} showTooltip />
                        <Text variant="small" color="foreground.subtle">
                            {translate('offerPositions.deliveryDate', {
                                date: formatDateTime({
                                    date: position.offerDateDelivery,
                                    format: 'PP',
                                }),
                            })}
                        </Text>
                    </PositionListItemWrapper>
                </PositionItem>
            );

        case PositionType.VESSEL:
            return (
                <PositionItem onClick={onClick}>
                    <PositionListItemWrapper
                        title={translate('offerPositions.currentPosition')}
                        icon={<MarkerIcon color={markerColor} />}
                    >
                        <Text variant="small" color="foreground.subtle">
                            {translate('offerPositions.updated', {
                                counter: formatFromNow(position.vessel.locationData.datePositionReceived),
                            })}
                        </Text>
                    </PositionListItemWrapper>
                </PositionItem>
            );

        case PositionType.NEXT_PORT:
            return (
                <PositionItem onClick={onClick}>
                    <PositionListItemWrapper
                        title={translate('offerPositions.nextPort')}
                        icon={<MarkerIcon color={markerColor} />}
                    >
                        <Port port={position.port} vesselId={position.vesselId} showTooltip />
                        <Text variant="small" color="foreground.subtle">
                            {translate('offerPositions.eta', {
                                date: formatDateTime({
                                    date: position.eta!,
                                    timeZoneId: position.port.timeZoneId,
                                }),
                            })}
                        </Text>
                    </PositionListItemWrapper>
                </PositionItem>
            );

        case PositionType.LAST_PORT:
            return (
                <PositionItem onClick={onClick}>
                    <PositionListItemWrapper
                        title={translate('offerPositions.lastPort')}
                        icon={<MarkerIcon color={markerColor} />}
                        disabled
                    >
                        <Port
                            port={position.port}
                            vesselId={position.vesselId}
                            highlightColor="foreground.disabled"
                            showTooltip
                        />
                        <Text variant="small">
                            {translate('offerPositions.eta', {
                                date: formatDateTime({
                                    date: position.eta!,
                                    timeZoneId: position.port.timeZoneId,
                                }),
                            })}
                        </Text>
                    </PositionListItemWrapper>
                </PositionItem>
            );
        default:
            return null;
    }
};
